import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader2, TrendingUp, HelpCircle } from 'lucide-react';
import { Market, MarketOption, UserProfile } from '../types.ts';
import { useSettings } from '../lib/settings/settingsContext.tsx';
import { supabase } from '../lib/db.ts';
import { logger } from '../services/logger.ts';
import { getMarketQuote, placeLMSRBet, type MarketQuoteResponse } from '../lib/api.ts';
import { lmsrPrice, solveSharesOutForLMSRBuy } from '../lib/lmsrMath.ts';
import { formatRateAsPercent, performanceFeeRateOnProfit } from '../lib/sharePricedFees.ts';
import { formatSharePriceZar, formatSharesExact2Label } from '../lib/displayFormat.ts';
import { isBelowMinTradeZar } from '../lib/tradeLimits.ts';
import { useMinTradeZar } from '../hooks/useMinTradeZar.ts';

interface LMSRBuyPanelProps {
  market: Market;
  userProfile: UserProfile | null;
  selectedOptionId: number | null;
  onCancel: () => void;
  onBetSuccess?: () => void | Promise<void>;
}

function InfoTooltip({ label, content }: { label: string; content: string }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="relative inline-flex items-center">
      <button
        type="button"
        aria-label={label}
        aria-describedby={open ? 'info-tooltip-panel' : undefined}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="inline-flex items-center justify-center rounded-full p-0.5 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-wayja-teal/50"
      >
        <HelpCircle className="w-3.5 h-3.5" />
      </button>
      {open && (
        <span
          id="info-tooltip-panel"
          role="tooltip"
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-50 w-64 rounded-md bg-gray-900 px-3 py-2 text-xs font-normal text-white shadow-lg pointer-events-none"
        >
          {content}
          <span className="absolute left-1/2 top-full -translate-x-1/2 -mt-px h-2 w-2 rotate-45 bg-gray-900" />
        </span>
      )}
    </span>
  );
}

function mergeOutstandingShares(
  prev: MarketOption[],
  rows: { id: number; outstanding_shares?: number | null }[]
): MarketOption[] {
  return prev.map((o) => {
    const row = rows.find((r) => r.id === o.id);
    if (!row || row.outstanding_shares === undefined || row.outstanding_shares === null) return o;
    return { ...o, outstanding_shares: Number(row.outstanding_shares) };
  });
}

const AMOUNT_INCREMENTS = [10, 50, 200];

export function LMSRBuyPanel({ market, userProfile, selectedOptionId, onCancel, onBetSuccess }: LMSRBuyPanelProps) {
  const { formatCurrency, settings } = useSettings();
  const minTradeZar = useMinTradeZar();
  const [betAmount, setBetAmount] = useState('');
  const [isPlacing, setIsPlacing] = useState(false);
  const [isQuoting, setIsQuoting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [quote, setQuote] = useState<MarketQuoteResponse | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const performanceFeeOnProfit = useMemo(() => performanceFeeRateOnProfit(market), [market]);

  const [liveOptions, setLiveOptions] = useState<MarketOption[]>(() => market.market_options ?? []);

  useEffect(() => {
    setLiveOptions(market.market_options ?? []);
  }, [market.market_options]);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      const role = data?.user?.app_metadata?.role;
      setIsAdmin(role === 'admin' || role === 'superuser');
    });
  }, []);

  useEffect(() => {
    const mid = market.id;

    const refreshSharesFromDb = async () => {
      const { data, error: qErr } = await supabase
        .from('market_options')
        .select('id, outstanding_shares')
        .eq('market_id', mid)
        .eq('status', 'active');
      if (qErr || !data) return;
      setLiveOptions((prev) => mergeOutstandingShares(prev, data));
    };

    const channel = supabase
      .channel(`fixed-price-bets-${mid}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'market_options',
          filter: `market_id=eq.${mid}`,
        },
        (payload) => {
          if (payload.new && typeof payload.new === 'object' && 'id' in payload.new) {
            const row = payload.new as Partial<MarketOption> & { id: number };
            setLiveOptions((prev) =>
              prev.map((o) => (o.id === row.id ? { ...o, ...row } : o))
            );
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'user_bets',
          filter: `market_id=eq.${mid}`,
        },
        () => {
          void refreshSharesFromDb();
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [market.id]);

  const options = liveOptions;
  const b = market.lmsr_b ?? 100;
  const shares = useMemo(() => options.map((o) => Number(o.outstanding_shares ?? 0)), [options]);
  const selectedOption = options.find((o) => o.id === selectedOptionId);
  const optionIdx = options.findIndex((o) => o.id === selectedOptionId);

  const amount = parseFloat(betAmount) || 0;
  const tradeBudget = amount;
  const walletBalance = userProfile?.wallet_balance ?? 0;
  const belowMinTrade = isBelowMinTradeZar(amount, minTradeZar);
  const isInsufficientFunds = amount > 0 && amount > walletBalance;

  const sharesOut = useCallback((): number => {
    if (optionIdx < 0 || tradeBudget <= 0) return 0;
    return solveSharesOutForLMSRBuy(b, shares, optionIdx, tradeBudget);
  }, [b, shares, optionIdx, tradeBudget]);

  const currentPrice =
    optionIdx >= 0 && shares.length > 0 ? lmsrPrice(b, shares, optionIdx) : 1 / Math.max(options.length, 2);

  const previewShares = sharesOut();

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!selectedOptionId || amount <= 0) {
        setQuote(null);
        return;
      }
      setIsQuoting(true);
      try {
        const q = await getMarketQuote({
          market_id: market.id,
          option_id: selectedOptionId,
          stake: amount,
        });
        if (!cancelled) setQuote(q);
      } catch {
        if (!cancelled) setQuote(null);
      } finally {
        if (!cancelled) setIsQuoting(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [amount, selectedOptionId, market.id]);

  const handlePlaceBet = async () => {
    if (!selectedOption || amount <= 0) return;
    if (amount > walletBalance) return;
    setError(null);
    setSuccess(null);
    setIsPlacing(true);
    try {
      const result = await placeLMSRBet({
        option_id: selectedOption.id,
        amount,
        market_id: market.id,
      });
      if (!result.success) throw new Error((result as { error?: string }).error ?? 'Trade failed');
      const sharesReceived = result.shares_out ?? previewShares;
      if (typeof result.new_balance === 'number' && !Number.isNaN(result.new_balance)) {
        window.dispatchEvent(
          new CustomEvent('walletBalanceUpdated', { detail: { balance: result.new_balance } }),
        );
      }
      setSuccess(
        `Trade placed! You received ${formatSharesExact2Label(sharesReceived)} of "${selectedOption.name}"`,
      );
      setBetAmount('');
      if (onBetSuccess) await onBetSuccess();
    } catch (err: unknown) {
      logger.error('Fixed-price bet error:', err);
      setError(err instanceof Error ? err.message : 'Failed to place trade');
    } finally {
      setIsPlacing(false);
    }
  };

  if (market.status !== 'active') {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-5">
        <p className="text-gray-500 text-sm">This market is not accepting trades.</p>
        <button onClick={onCancel} className="mt-3 px-4 py-2 bg-gray-200 rounded-md text-sm">Go Back</button>
      </div>
    );
  }

  const isBuyDisabled = !selectedOption || amount <= 0 || belowMinTrade || isInsufficientFunds || isPlacing || isAdmin;

  return (
    <div className="bg-white border border-wayja-teal/30 rounded-lg p-5 relative z-30 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-wayja-light-teal/40 text-wayja-dark-teal border border-wayja-teal/40">
          <TrendingUp className="w-3 h-3" />
          Live price
        </span>
        <h3 className="text-xl font-medium text-gray-900">Place your trade</h3>
      </div>

      {isAdmin && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-blue-700 text-sm">
          <strong>Admin Notice:</strong> Admins cannot place trades.
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm animate-pulse">{error}</div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm animate-pulse">{success}</div>
      )}

      <div className="mb-5 p-4 bg-wayja-light-teal/20 border border-wayja-light-teal/50 rounded-md">
        <div className="flex justify-between items-center">
          <div>
            <p className="text-xs text-gray-500 mb-0.5">Selected outcome</p>
            <p className="font-medium text-lg">{selectedOption?.name ?? 'None selected'}</p>
          </div>
          {selectedOption && (
            <div className="text-right">
              <p className="text-xs text-gray-500 mb-0.5">Current price</p>
              <p className="font-bold text-wayja-dark-teal text-lg flex items-center gap-1 justify-end">
                <TrendingUp className="w-4 h-4" />
                R{formatSharePriceZar(currentPrice)}/share
              </p>
              <p className="text-xs text-gray-400">
                {quote?.fair_probability !== undefined
                  ? `${(quote.fair_probability * 100).toFixed(1)}% fair chance`
                  : `${(currentPrice * 100).toFixed(1)}% chance`}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="mb-5">
        <div className="flex justify-between items-center mb-2">
          <label className="text-base font-medium text-gray-900">Amount (ZAR)</label>
          <span className="text-sm text-gray-600">
            Balance: <span className="font-semibold">{formatCurrency(userProfile?.wallet_balance ?? 0)}</span>
          </span>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-3 shadow-sm focus-within:ring-2 focus-within:ring-wayja-teal/50">
          <div className="flex items-center justify-between">
            <span className="text-wayja-teal text-2xl font-medium">{settings.currency.symbol}</span>
            <input
              type="number"
              className="bg-transparent text-gray-900 text-right text-4xl w-full focus:outline-none placeholder-gray-400"
              value={betAmount}
              onChange={(e) => { setBetAmount(e.target.value); setError(null); }}
            min={minTradeZar}
            step="0.01"
              disabled={isPlacing}
              placeholder="0.00"
            />
          </div>
        </div>
        {belowMinTrade && (
          <p className="text-sm text-amber-700 mb-2" role="alert">
            Minimum trade amount is R{minTradeZar}.
          </p>
        )}
        {isInsufficientFunds && (
          <p className="text-sm text-amber-700 mb-2" role="alert">
            Insufficient balance
          </p>
        )}
        <div className="grid grid-cols-4 gap-2">
          {AMOUNT_INCREMENTS.map(inc => (
            <button
              key={inc}
              onClick={() => setBetAmount((v) => ((parseFloat(v) || 0) + inc).toFixed(2))}
              disabled={isPlacing}
              className="bg-white hover:bg-gray-50 text-gray-700 py-2 px-3 rounded-lg text-sm font-medium border border-gray-200 hover:scale-105 active:scale-95 transition-all disabled:opacity-50"
            >
              +{settings.currency.symbol}{inc}
            </button>
          ))}
          <button
            onClick={() => setBetAmount((userProfile?.wallet_balance ?? 0).toFixed(2))}
            disabled={isPlacing}
            className="bg-white hover:bg-gray-50 text-wayja-teal py-2 px-3 rounded-lg text-sm font-medium border border-gray-200 hover:scale-105 active:scale-95 transition-all disabled:opacity-50"
          >
            Max
          </button>
        </div>
      </div>

      {selectedOption && amount > 0 && (
        <div className="mb-5 bg-gray-50 rounded-lg border border-gray-200 p-4 space-y-2">
          <div className="flex justify-between text-sm font-semibold">
            <span className="text-gray-800">You pay</span>
            <span className="text-gray-900">{formatCurrency(tradeBudget)}</span>
          </div>
          <div className="flex justify-between text-sm font-semibold pt-1 border-t border-gray-200">
            <span className="text-gray-800">You receive</span>
            <span className="text-wayja-dark-teal">
              {formatSharesExact2Label(quote?.shares_received ?? previewShares)}
            </span>
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span className="inline-flex items-center gap-1">
              Cost per share
              <InfoTooltip
                label="Spread pricing help"
                content="The share price shown includes a small platform spread. This is why you pay slightly more per share than the market probability would suggest. The spread is our fee for providing instant liquidity on every trade."
              />
            </span>
            <span>
              {quote?.cost_per_share
                ? formatCurrency(quote.cost_per_share)
                : previewShares > 0
                  ? formatCurrency(tradeBudget / previewShares)
                  : '—'}
            </span>
          </div>
          <p className="text-xs text-gray-500 leading-snug -mt-1">
            Price increases as you buy more shares — your average reflects the price movement across your entire purchase.
          </p>
          <div className="flex justify-between text-xs text-gray-500">
            <span>Payout if this outcome wins</span>
            <span className="text-green-600 font-medium">
              {formatCurrency(quote?.potential_payout ?? (quote?.shares_received ?? previewShares))}
            </span>
          </div>
          {(quote?.shares_received ?? previewShares) > 0 && (
            <div className="flex justify-between text-xs text-gray-500 gap-2">
              <span className="inline-flex items-center gap-1">
                If you win: est. fee on profit ({formatRateAsPercent(performanceFeeOnProfit)}) — not charged now
                <InfoTooltip
                  label="Fee on profit help"
                  content="This is only an estimate of the performance fee that would apply to profit (payout minus stake) if this outcome wins at resolution. Your wallet is debited your stake only when you place this trade."
                />
              </span>
              <span>
                {formatCurrency(
                  quote?.projected_performance_fee_if_win ??
                    Math.max(0, (quote?.shares_received ?? previewShares) - tradeBudget) * performanceFeeOnProfit
                )}
              </span>
            </div>
          )}
          <p className="text-xs text-gray-400 pt-1 border-t border-gray-200">
            {isQuoting
              ? 'Refreshing quote...'
              : 'The full amount you enter buys shares. A performance fee applies only to profit at resolution or if you sell at a gain—if you do not profit, you pay nothing on that leg.'}
          </p>
        </div>
      )}

      <div className="flex justify-end gap-3">
        {onCancel && (
          <button
            onClick={onCancel}
            disabled={isPlacing}
            className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all disabled:opacity-50"
          >
            Cancel
          </button>
        )}
        <button
          onClick={handlePlaceBet}
          disabled={isBuyDisabled}
          className={`px-5 py-2.5 text-white rounded-lg transition-all font-medium shadow-sm flex items-center justify-center ${
            isBuyDisabled
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-wayja-teal hover:bg-wayja-dark-teal active:scale-95'
          }`}
        >
          {isPlacing ? <Loader2 className="animate-spin mr-2 h-4 w-4" /> : null}
          {isPlacing ? 'Placing trade…' : 'Place trade'}
        </button>
      </div>
    </div>
  );
}
